import axios from 'axios'

import router from '@/router'
import { contentLockBlocks, contentLockError, noteServerLock } from '@/helpers/content-lock'
import { getActiveCompanyId, recoverCompanySession } from '@/helpers/companySession'

axios.interceptors.response.use(
  function (response) {
		return response
  },
  function (error) {
		// handle error
		if (error || error.response) {
			return Promise.reject(error)
		}
  }
)

function catchMiddleware(err) {
  // El servidor rechazó por lock: la pantalla se entera aunque el bloqueo del
  // cliente no haya llegado a tiempo (lock recién tomado, pestaña vieja).
  if (err && err.response && err.response.status === 423) {
    noteServerLock(err.response.data && err.response.data.lock)
  }

  if (err && err.response && err.response.status === 441) {
		// Password expired and must change it
  }
  else if (err && err.response && err.response.status === 401) {
		//Unauthorized
		localStorage.removeItem('auth.currentUser');
		sessionStorage.removeItem('current_project');

		router.push('/login') 
	}
  else if (err && err.response && err.response.status === 403 && err.response.data && err.response.data.code === 'COMPANY_NOT_ALLOWED') {
		// La cuenta de esta pestaña ya no es del usuario: la pestaña vuelve a una que sí.
		recoverCompanySession()
	}

  return new Promise((_, reject) => {
		reject(err)
  })
}

/**
 * Cortafuegos de escritura: acá pasan TODOS los guardados del admin, así que
 * es el único lugar donde hay que acordarse del lock. Un botón de guardar
 * nuevo queda cubierto sin tocar nada.
 */
function guardWrite(method, url) {
  if (contentLockBlocks(method, url)) {
    return Promise.reject(contentLockError())
  }
  return null
}

/*
 * Cuenta por pestaña: cada llamada autenticada a nuestra API dice sobre qué
 * cuenta trabaja ESTA pestaña (header `x-company`, ver helpers/companySession).
 * Así dos pestañas del mismo login pueden estar en cuentas distintas sin pisarse.
 *
 * Solo va a nuestro backend y solo con sesión: a otros hosts un header propio
 * les rompe el preflight de CORS. Elegir cuenta y cerrar sesión no lo llevan,
 * para que una cuenta que el usuario ya no tiene no le impida salir de ella.
 */
const COMPANY_HEADER = 'x-company'
const NO_COMPANY_HEADER = /\/api\/auth\/(select-company|logout)(\?|$)/

function backendBase() {
  return (typeof window !== 'undefined' && window.config && window.config.VUE_APP_BACKEND) || ''
}

function withCompany(url, config) {
  const backend = backendBase()
  if (!backend || typeof url !== 'string' || !url.startsWith(backend) || NO_COMPANY_HEADER.test(url)) return config

  const headers = (config && config.headers) || {}
  const names = Object.keys(headers).map(k => k.toLowerCase())
  if (!names.includes('authorization') || names.includes(COMPANY_HEADER)) return config

  const company = getActiveCompanyId()
  if (!company) return config
  return { ...config, headers: { ...headers, [COMPANY_HEADER]: company } }
}

const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;

async function retryRequest(requestFn, retryCount = 0) {
  try {
    return await requestFn();
  } catch (error) {
    if (error?.response?.status >= 500 && retryCount < MAX_RETRIES) {
      const delay = INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
      await new Promise(resolve => setTimeout(resolve, delay));
      return retryRequest(requestFn, retryCount + 1);
    }
    throw error;
  }
}

/*
 * GETs compartidos y caché corta.
 *
 * - Dedup: dos GET idénticos en vuelo (misma URL, headers —incluye el token y
 *   la cuenta de la pestaña—, params, responseType y proyecto actual) comparten
 *   UNA request. La entrada se borra al resolverse.
 * - Caché con TTL, solo para las URLs de GET_CACHE_RULES (analytics, atributos
 *   custom, proyecto). Nada que sea export/descarga/CSV.
 * - Cualquier escritura (post/put/patch/delete) vacía todo, al empezar y al
 *   terminar; un GET que arrancó antes de la escritura no guarda su resultado.
 * - Cada caller recibe su propia copia de `data`: hay pantallas que mutan lo
 *   que reciben y no pueden pisarse entre sí ni pisar lo cacheado.
 */
const GET_CACHE_RULES = [
  { test: /\/api\/items\/custom-attributes(\?|$)/, ttl: 60 * 1000 },
  { test: /\/api\/projects\/[0-9a-f]{24}(\?|$)/i, ttl: 60 * 1000 },
  { test: /\/api\/(analytics|search-analytics|liveshopping-analytics)\//, ttl: 120 * 1000 }
]
const NEVER_CACHE = /export|download|csv/i
// POSTs que solo leen (consultas con cuerpo): no invalidan la caché.
const READ_ONLY_POSTS = /\/api\/audiences\/(explore|customer-reachability)(\?|$)|\/api\/custom-strategies\/related-products\/preview(\?|$)/
const SHAREABLE_RESPONSE_TYPES = [undefined, null, '', 'json', 'text']
const MAX_CACHE_ENTRIES = 200

const inflightGets = new Map()
const cachedGets = new Map()
let cacheGeneration = 0
let cacheProject = null

function readCurrentProject() {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('current_project') : null
  } catch (e) {
    return null
  }
}

function clearRequestCache() {
  cacheGeneration++
  cachedGets.clear()
  inflightGets.clear()
}

function cloneData(value) {
  if (Array.isArray(value)) return value.map(cloneData)
  if (value && typeof value === 'object') {
    const proto = Object.getPrototypeOf(value)
    if (proto !== Object.prototype && proto !== null) return value
    const out = {}
    Object.keys(value).forEach(k => { out[k] = cloneData(value[k]) })
    return out
  }
  return value
}

function cloneResponse(response) {
  if (!response || typeof response !== 'object') return response
  return { ...response, data: cloneData(response.data) }
}

function sortedHeaders(headers) {
  if (!headers || typeof headers !== 'object') return null
  const out = {}
  Object.keys(headers).sort().forEach(k => { out[k.toLowerCase()] = headers[k] })
  return out
}

function getShareKey(url, config) {
  const cfg = config || {}
  if (cfg.cancelToken || cfg.signal || cfg.onDownloadProgress) return null
  if (!SHAREABLE_RESPONSE_TYPES.includes(cfg.responseType)) return null
  return JSON.stringify([
    url,
    sortedHeaders(cfg.headers),
    cfg.params || null,
    cfg.responseType || null,
    cfg.baseURL || null,
    readCurrentProject()
  ])
}

function getCacheTtl(url, config) {
  const cfg = config || {}
  if (cfg.responseType && cfg.responseType !== 'json') return 0
  if (typeof url !== 'string' || NEVER_CACHE.test(url)) return 0
  const rule = GET_CACHE_RULES.find(r => r.test.test(url))
  return rule ? rule.ttl : 0
}

function checkProjectChange() {
  const project = readCurrentProject()
  if (project !== cacheProject) {
    cacheProject = project
    cachedGets.clear()
  }
}

function sharedGet(url, config, doRequest) {
  const key = getShareKey(url, config)
  if (!key) return doRequest().catch(catchMiddleware)

  checkProjectChange()
  const ttl = getCacheTtl(url, config)

  if (ttl) {
    const hit = cachedGets.get(key)
    if (hit && hit.expires > Date.now()) {
      return Promise.resolve(cloneResponse(hit.response))
    }
    if (hit) cachedGets.delete(key)
  }

  let entry = inflightGets.get(key)
  if (!entry) {
    const generation = cacheGeneration
    entry = { waiters: 0, cached: false }
    const current = entry
    current.promise = doRequest().then(response => {
      if (inflightGets.get(key) === current) inflightGets.delete(key)
      if (ttl && generation === cacheGeneration) {
        if (cachedGets.size >= MAX_CACHE_ENTRIES) cachedGets.delete(cachedGets.keys().next().value)
        cachedGets.set(key, { response, expires: Date.now() + ttl })
        current.cached = true
      }
      return response
    }, error => {
      if (inflightGets.get(key) === current) inflightGets.delete(key)
      throw error
    })
    inflightGets.set(key, current)
  }
  entry.waiters++
  const joined = entry
  // Un solo caller y nada guardado: recibe el objeto tal cual, como antes.
  return joined.promise
    .then(response => (joined.waiters === 1 && !joined.cached ? response : cloneResponse(response)))
    .catch(catchMiddleware)
}

function write(method, url, data, config) {
  const blocked = guardWrite(method, url)
  if (blocked) return blocked
  const invalidates = !(method === 'post' && typeof url === 'string' && READ_ONLY_POSTS.test(url))
  if (invalidates) clearRequestCache()
  // Single-use auth tokens must not be replayed automatically after a 5xx.
  const { skipRetry, ...requestConfig } = config || {}
  const request = method === 'delete'
    ? () => axios({ method, url, ...requestConfig })
    : () => axios({ method, url, data, ...requestConfig })
  return (skipRetry ? request() : retryRequest(request))
    .then(response => { if (invalidates) clearRequestCache(); return response }, error => { if (invalidates) clearRequestCache(); throw error })
    .catch(catchMiddleware)
}

export { clearRequestCache }

export default {
  get(url, requestConfig) {
    // Con la cuenta en los headers, la clave de dedup/caché ya queda separada por cuenta.
    const config = withCompany(url, requestConfig)
    return sharedGet(url, config, () => retryRequest(() =>
      axios({
        method: 'get',
        url,
        ...config
      })
    ))
  },
  post(url, data, config) {
    return write('post', url, data, withCompany(url, config))
  },
  put(url, data, config) {
    return write('put', url, data, withCompany(url, config))
  },
  patch(url, data, config) {
    return write('patch', url, data, withCompany(url, config))
  },
  delete(url, config) {
    return write('delete', url, undefined, withCompany(url, config))
  }
}
